import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { FiMail, FiPhone, FiMapPin, FiUser, FiBriefcase, FiLayers } from 'react-icons/fi';

const SUBJECT_OPTIONS = [
  'Income Tax',
  'Audit',
  'Service Tax',
  'Company Law',
  'Excise Duty',
  'Customs',
  'Wealth Tax',
  'Sales Tax/Value Added Tax',
  'FEMA',
  'RBI',
  'Labour Law',
  'Others'
];

export default function Query() {
  const [formData, setFormData] = useState({
    name: '',
    designation: '',
    organization: '',
    officeAddress: '',
    city: '',
    email: '',
    telephone: '',
    mobile: '',
    update: 'Yes', // default to Yes as on original site
    subject: '',
    query: ''
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    // Clear error for field on change
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobile.trim())) {
      newErrors.mobile = 'Mobile number must be exactly 10 digits';
    }

    if (formData.telephone.trim() && !/^\d+$/.test(formData.telephone.trim())) {
      newErrors.telephone = 'Telephone number must contain digits only';
    }

    if (!formData.subject) {
      newErrors.subject = 'Please select a subject';
    }

    if (!formData.query.trim()) {
      newErrors.query = 'Query/Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      let body = '';
      if (formData.city) body += `City: ${formData.city}\n`;
      if (formData.designation) body += `Designation: ${formData.designation}\n`;
      if (formData.organization) body += `Organization: ${formData.organization}\n`;
      if (formData.mobile) body += `Mobile: ${formData.mobile}\n`;
      if (formData.telephone) body += `Telephone: ${formData.telephone}\n`;
      if (formData.officeAddress) body += `Office Address: ${formData.officeAddress}\n`;
      if (formData.update) body += `Wants updates: ${formData.update}\n`;
      if (formData.query) body += `\nQuery:\n${formData.query}`;

      emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          title: 'Query Sheet Submission - ' + formData.subject,
          name: formData.name,
          email: formData.email,
          message: body.trim()
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      )
      .then((response) => {
        setSubmitted(true);
        setTimeout(() => setSubmitted(false), 30000);
        setFormData({
          name: '',
          designation: '',
          organization: '',
          officeAddress: '',
          city: '',
          email: '',
          telephone: '',
          mobile: '',
          update: 'Yes',
          subject: '',
          query: ''
        });
      })
      .catch((error) => {
        console.error('EmailJS Error:', error);
        alert('Failed to send query. Please try again.');
      });
    }
  };

  return (
    <div className="page-wrapper">
      <div className="page-hero">
        <h1>Query <span>Sheet</span></h1>
        <p>Have a professional question? Submit your query and our experts will advise you.</p>
      </div>

      <main className="query-page-main">
        <div className="container">
          <div className="query-form-card">
            <div className="query-form-header">
              <span className="section-badge">Get Expert Advice</span>
              <h2>Submit Your Query</h2>
              <div className="gold-divider" style={{ margin: '8px auto 24px' }} />
              <p>Please fill out the form below. Fields marked with an asterisk (<span className="text-danger">*</span>) are required.</p>
            </div>

            {submitted && (
              <div className="alert alert-success query-success-alert">
                <h4>Success!</h4>
                <p>Your query has been submitted successfully. We will get back to you soon.</p>
                <button className="btn-close" onClick={() => setSubmitted(false)}>Dismiss</button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="query-form">
              <fieldset className="query-fieldset">
                <legend className="query-legend">:: Query Sheet ::</legend>

                <div className="form-grid">
                  {/* Name */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="query-name">
                      Name <span className="text-danger">*</span>
                    </label>
                    <div className="input-with-icon">
                      <FiUser className="input-icon" />
                      <input
                        type="text"
                        id="query-name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        className={`form-input ${errors.name ? 'input-error' : ''}`}
                      />
                    </div>
                    {errors.name && <span className="error-message">{errors.name}</span>}
                  </div>

                  {/* Designation */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="query-designation">
                      Designation
                    </label>
                    <div className="input-with-icon">
                      <FiBriefcase className="input-icon" />
                      <input
                        type="text"
                        id="query-designation"
                        name="designation"
                        value={formData.designation}
                        onChange={handleChange}
                        placeholder="e.g. Manager, Director"
                        className="form-input"
                      />
                    </div>
                  </div>

                  {/* Organization */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="query-organization">
                      Organization
                    </label>
                    <div className="input-with-icon">
                      <FiLayers className="input-icon" />
                      <input
                        type="text"
                        id="query-organization"
                        name="organization"
                        value={formData.organization}
                        onChange={handleChange}
                        placeholder="Company name"
                        className="form-input"
                      />
                    </div>
                  </div>

                  {/* City */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="query-city">
                      City <span className="text-danger">*</span>
                    </label>
                    <div className="input-with-icon">
                      <FiMapPin className="input-icon" />
                      <input
                        type="text"
                        id="query-city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        placeholder="City"
                        className={`form-input ${errors.city ? 'input-error' : ''}`}
                      />
                    </div>
                    {errors.city && <span className="error-message">{errors.city}</span>}
                  </div>

                  {/* Office Address */}
                  <div className="form-group form-group-full">
                    <label className="form-label" htmlFor="query-address">
                      Office Address
                    </label>
                    <div className="input-with-icon">
                      <FiMapPin className="input-icon" />
                      <input
                        type="text"
                        id="query-address"
                        name="officeAddress"
                        value={formData.officeAddress}
                        onChange={handleChange}
                        placeholder="Full office or business address"
                        className="form-input"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="query-email">
                      E-mail address <span className="text-danger">*</span>
                    </label>
                    <div className="input-with-icon">
                      <FiMail className="input-icon" />
                      <input
                        type="email"
                        id="query-email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="example@mail.com"
                        className={`form-input ${errors.email ? 'input-error' : ''}`}
                      />
                    </div>
                    {errors.email && <span className="error-message">{errors.email}</span>}
                  </div>

                  {/* Telephone No */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="query-telephone">
                      Telephone No.
                    </label>
                    <div className="input-with-icon">
                      <FiPhone className="input-icon" />
                      <input
                        type="text"
                        id="query-telephone"
                        name="telephone"
                        value={formData.telephone}
                        onChange={handleChange}
                        placeholder="Landline number"
                        className={`form-input ${errors.telephone ? 'input-error' : ''}`}
                      />
                    </div>
                    {errors.telephone && <span className="error-message">{errors.telephone}</span>}
                  </div>

                  {/* Mobile */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="query-mobile">
                      Mobile <span className="text-danger">*</span>
                    </label>
                    <div className="input-with-icon">
                      <FiPhone className="input-icon" />
                      <input
                        type="text"
                        id="query-mobile"
                        name="mobile"
                        value={formData.mobile}
                        onChange={handleChange}
                        placeholder="10-digit mobile number"
                        className={`form-input ${errors.mobile ? 'input-error' : ''}`}
                      />
                    </div>
                    {errors.mobile && <span className="error-message">{errors.mobile}</span>}
                  </div>

                  {/* Other professional updates */}
                  <div className="form-group">
                    <label className="form-label">
                      Other professional updates <span className="text-danger">*</span>
                    </label>
                    <div className="radio-group">
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="update"
                          value="Yes"
                          checked={formData.update === 'Yes'}
                          onChange={handleChange}
                          className="form-radio"
                        />
                        Yes
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="update"
                          value="No"
                          checked={formData.update === 'No'}
                          onChange={handleChange}
                          className="form-radio"
                        />
                        No
                      </label>
                    </div>
                  </div>

                  {/* Subject of Query */}
                  <div className="form-group form-group-full">
                    <label className="form-label" htmlFor="query-subject">
                      Subject of Query <span className="text-danger">*</span>
                    </label>
                    <select
                      id="query-subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className={`form-select ${errors.subject ? 'input-error' : ''}`}
                    >
                      <option value="" disabled hidden style={{ display: 'none' }}>Please Select</option>
                      {SUBJECT_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                    {errors.subject && <span className="error-message">{errors.subject}</span>}
                  </div>

                  {/* Query */}
                  <div className="form-group form-group-full">
                    <label className="form-label" htmlFor="query-text">
                      Query <span className="text-danger">*</span>
                    </label>
                    <textarea
                      id="query-text"
                      name="query"
                      value={formData.query}
                      onChange={handleChange}
                      placeholder="Type your query or message details here..."
                      rows="6"
                      className={`form-textarea ${errors.query ? 'input-error' : ''}`}
                    />
                    {errors.query && <span className="error-message">{errors.query}</span>}
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-primary form-submit-btn">
                    Submit Query
                  </button>
                </div>
              </fieldset>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
